/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import * as XLSX from 'xlsx';

// --- SVG Icons ---
const Icons = {
    Chart: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
    Check: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Save: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>,
    Print: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>,
    Trash: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
};

export default function Subjectdetail() {
    const { id } = useParams();

    // --- State ---
    const [students, setStudents] = useState([]);
    const [sections, setSections] = useState([]);
    const [sectionMarks, setSectionMarks] = useState([]);
    const [marksBeingEdited, setMarksBeingEdited] = useState({});
    const [subjectname, setSubjectName] = useState('Loading...');
    const [subjectGrade, setSubjectGrade] = useState('');
    const [subjectClass, setSubjectClass] = useState('');
    const [loading, setLoading] = useState(true);

    // UI State
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvProgress, setCsvProgress] = useState('');
    const [saveAllDisabled, setSaveAllDisabled] = useState(false);

    // Forms
    const [newStudent, setNewStudent] = useState({ firstName: '', lastName: '' });
    const [newSection, setNewSection] = useState({ section_name: '', weight: 1, max_mark: 100 });
    const [addingSection, setAddingSection] = useState(false);

    // Debounce State
    const [sectionWeights, setSectionWeights] = useState({});
    const [sectionMaxMarks, setSectionMaxMarks] = useState({});

    // Refs
    const markInputRefs = useRef({});
    const weightTimersRef = useRef({});
    const maxMarkTimersRef = useRef({});
    const sectionMarksRef = useRef(sectionMarks);

    // Sync Ref
    useEffect(() => {
        sectionMarksRef.current = sectionMarks;
    }, [sectionMarks]);

    // --- Helpers ---
    const calculateLevel = useCallback((percentage) => {
        // Round to nearest integer before calculating level to avoid edge cases like 79.9
        const rounded = Math.round(percentage);
        if (rounded >= 80) return 7;
        if (rounded >= 70) return 6;
        if (rounded >= 60) return 5;
        if (rounded >= 50) return 4;
        if (rounded >= 40) return 3;
        if (rounded >= 30) return 2;
        return 1;
    }, []);

    const levelToSymbol = useCallback((level) => {
        switch (level) {
            case 7: return 'A*';
            case 6: return 'A';
            case 5: return 'B';
            case 4: return 'C';
            case 3: return 'D';
            case 2: return 'E';
            case 1: return 'F';
            default: return level;
        }
    }, []);

    const formatGrade = useCallback((level) => {
        const gradeDisplayFormat = localStorage.getItem('gradeDisplayFormat') || 'levels';
        return gradeDisplayFormat === 'symbols' ? levelToSymbol(level) : level;
    }, [levelToSymbol]);

    const sanitizeSheetName = (name) => name.replace(/[:\\/?*\[\]]/g, '_').slice(0, 30);

    // --- Calculate Stats Per Section ---
    const getSectionStats = (section) => {
        const validMarks = [];

        students.forEach(student => {
            const markObj = sectionMarks.find(m =>
                parseInt(m.student_id) === parseInt(student.student_id) &&
                parseInt(m.section_id) === parseInt(section.id)
            );

            if (markObj && markObj.mark !== '' && markObj.mark != null && !isNaN(parseFloat(markObj.mark))) {
                validMarks.push(parseFloat(markObj.mark));
            }
        });

        if (validMarks.length === 0) return null;

        const count = validMarks.length;
        const max = Math.max(...validMarks);
        const min = Math.min(...validMarks);
        const sum = validMarks.reduce((a, b) => a + b, 0);
        const avg = sum / count;
        const maxPossible = section.max_mark || 100;
        const avgPerc = (avg / maxPossible) * 100;

        return { count, max, min, avg, avgPerc };
    };

    // --- LOGIC: Grid Navigation ---
    const handleGridNavigation = (e, currentStudentId, currentSectionId) => {
        const allowedKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];
        if (!allowedKeys.includes(e.key)) return;

        e.preventDefault();

        const studentIndex = students.findIndex(s => String(s.student_id) === String(currentStudentId));
        const sectionIndex = sections.findIndex(s => String(s.id) === String(currentSectionId));

        let nextStudentId = currentStudentId;
        let nextSectionId = currentSectionId;

        if (e.key === 'ArrowDown' || e.key === 'Enter') {
            if (studentIndex < students.length - 1) nextStudentId = students[studentIndex + 1].student_id;
        } else if (e.key === 'ArrowUp') {
            if (studentIndex > 0) nextStudentId = students[studentIndex - 1].student_id;
        } else if (e.key === 'ArrowRight') {
            if (sectionIndex < sections.length - 1) nextSectionId = sections[sectionIndex + 1].id;
        } else if (e.key === 'ArrowLeft') {
            if (sectionIndex > 0) nextSectionId = sections[sectionIndex - 1].id;
        }

        if (
            markInputRefs.current[nextSectionId] &&
            markInputRefs.current[nextSectionId][nextStudentId] &&
            markInputRefs.current[nextSectionId][nextStudentId].current
        ) {
            const inputEl = markInputRefs.current[nextSectionId][nextStudentId].current;
            inputEl.focus();
            inputEl.select();
        }
    };

    // --- LOGIC: Bulk Save ---
    const handleBulkSave = async () => {
        setSaveAllDisabled(true);
        try {
            const marksToSave = sectionMarks
                .filter(mark => mark.mark !== '' && mark.mark != null && !isNaN(parseFloat(mark.mark)))
                .map(mark => {
                    const section = sections.find(s => String(s.id) === String(mark.section_id));
                    return {
                        student_id: mark.student_id,
                        section_id: mark.section_id,
                        subject_id: id,
                        mark: mark.mark,
                        max_mark: section ? section.max_mark : 100
                    };
                });

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/save_bulk_marks.php`, marksToSave);

            if (response.data && response.data.success) {
                alert(`Saved successfully! Updated ${response.data.updated} marks.`);
            } else {
                alert('Save failed: ' + (response.data?.error || 'Unknown error'));
            }
        } catch (error) {
            alert('Save error: ' + (error.response?.data?.error || error.message));
        } finally {
            setSaveAllDisabled(false);
        }
    };

    // --- LOGIC: Excel Upload ---
    const handleExcelUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setCsvUploading(true);
        setCsvProgress('Reading file...');

        const reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length < 2) {
                    alert('Excel file is empty or missing data rows');
                    setCsvUploading(false); return;
                }

                const header = jsonData[0].map(h => String(h).trim());
                const sectionColumns = header.slice(2).filter(h => h && h.length > 0);

                if (sectionColumns.length === 0) {
                    alert('Excel must have at least one section column after First Name and Last Name');
                    setCsvUploading(false); return;
                }

                let workingSections = [...sections];
                const sectionIndices = {};
                let sectionsCreated = 0;

                setCsvProgress('Analyzing sections...');

                for (let colIdx = 0; colIdx < sectionColumns.length; colIdx++) {
                    const colName = sectionColumns[colIdx];
                    const excelColIndex = colIdx + 2;

                    let existingSection = workingSections.find(s =>
                        s.section_name.toLowerCase() === colName.toLowerCase()
                    );

                    if (existingSection) {
                        sectionIndices[existingSection.id] = excelColIndex;
                    } else {
                        try {
                            const res = await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                                subject_id: id, term: 1, section_name: colName, weight: 1, max_mark: 100
                            });

                            if (res.data.success && res.data.section_id) {
                                const newSection = {
                                    id: res.data.section_id, section_name: colName, weight: 1, max_mark: 100
                                };
                                workingSections.push(newSection);
                                sectionIndices[newSection.id] = excelColIndex;
                                sectionsCreated++;
                            }
                        } catch (err) { }
                    }
                }

                if (Object.keys(sectionIndices).length === 0) {
                    alert('Could not match sections.'); setCsvUploading(false); return;
                }

                let successCount = 0;
                let errorCount = 0;
                let studentsCreated = 0;
                const totalRows = jsonData.length - 1;

                setCsvProgress(`Processing ${totalRows} students...`);

                const batchSize = 10;
                for (let batchStart = 1; batchStart < jsonData.length; batchStart += batchSize) {
                    const batchEnd = Math.min(batchStart + batchSize, jsonData.length);
                    const batchPromises = [];

                    for (let i = batchStart; i < batchEnd; i++) {
                        const row = jsonData[i];
                        if (!row || row.length < 2) continue;

                        const firstName = String(row[0] || '').trim();
                        const lastName = String(row[1] || '').trim();
                        if (!firstName || !lastName) continue;

                        const processStudent = async () => {
                            try {
                                let student = students.find(s =>
                                    s.first_name.toLowerCase() === firstName.toLowerCase() &&
                                    s.last_name.toLowerCase() === lastName.toLowerCase()
                                );

                                if (!student) {
                                    try {
                                        const addRes = await axios.post(`${process.env.REACT_APP_API_URL}/add_student_with_marks.php`, {
                                            subject_id: id, first_name: firstName, last_name: lastName
                                        });
                                        if (addRes.data.success) {
                                            student = { student_id: addRes.data.student_id, first_name: firstName, last_name: lastName };
                                            studentsCreated++;
                                        }
                                    } catch (e) { errorCount++; return; }
                                }

                                if (student) {
                                    for (const [sectionId, colIdx] of Object.entries(sectionIndices)) {
                                        const rawMark = row[colIdx];
                                        const mark = parseFloat(rawMark);
                                        if (!isNaN(mark)) {
                                            const markObj = sectionMarks.find(m =>
                                                parseInt(m.student_id) === parseInt(student.student_id) &&
                                                parseInt(m.section_id) === parseInt(sectionId)
                                            );
                                            try {
                                                if (markObj && markObj.id) {
                                                    await axios.put(`${process.env.REACT_APP_API_URL}/section_marks.php`, { id: markObj.id, mark: mark });
                                                } else {
                                                    await axios.post(`${process.env.REACT_APP_API_URL}/section_marks.php`, { student_id: student.student_id, section_id: parseInt(sectionId), mark: mark });
                                                }
                                                successCount++;
                                            } catch (err) { errorCount++; }
                                        }
                                    }
                                }
                            } catch (rowError) { errorCount++; }
                        };
                        batchPromises.push(processStudent());
                    }
                    await Promise.all(batchPromises);
                    const processed = Math.min(batchEnd - 1, totalRows);
                    setCsvProgress(`Processing... ${processed}/${totalRows}`);
                    await new Promise(resolve => setTimeout(resolve, 100)); // Rate limit
                }

                setCsvUploading(false);
                setCsvProgress('');
                alert(`Import Result:\nNew Sections: ${sectionsCreated}\nNew Students: ${studentsCreated}\nMarks Updated: ${successCount}`);
                window.location.reload();

            } catch (error) {
                setCsvUploading(false);
                alert('Error processing Excel: ' + error.message);
            }
        };
    };

    // --- Computed Data ---
    // --- MODIFIED: Added rawMarkSum and rawMaxSum calculation ---
    const studentsWithComputed = useMemo(() => {
        return students.map(student => {
            let weightedSum = 0;
            let weightSum = 0;

            // New variables for Raw Totals
            let rawMarkSum = 0;
            let rawMaxSum = 0;

            sections.forEach(section => {
                const markObj = sectionMarks.find(m =>
                    parseInt(m.student_id) === parseInt(student.student_id) &&
                    parseInt(m.section_id) === parseInt(section.id)
                );

                const mark = (markObj && markObj.mark !== '' && markObj.mark != null)
                    ? parseFloat(markObj.mark)
                    : null;

                const max = section.max_mark ? parseFloat(section.max_mark) : 100;
                const weight = section.weight ? parseFloat(section.weight) : 0;

                // 1. Calculate Weighted Average
                if (mark !== null && max > 0) {
                    const percent = (mark / max) * 100;
                    weightedSum += percent * weight;
                    weightSum += weight;

                    // 2. Calculate Raw Totals (The "Total Column")
                    rawMarkSum += mark;
                    rawMaxSum += max;
                }
            });

            // Calculate Final Weighted Average
            const average = weightSum > 0 ? weightedSum / weightSum : null;

            // Calculate Level based on the weighted average (using round to fix edge cases)
            const level = average !== null ? calculateLevel(average) : null;

            return {
                ...student,
                average,
                level,
                rawMarkSum,
                rawMaxSum
            };
        });
    }, [students, sectionMarks, sections, calculateLevel]);

    // --- Excel Export ---
    const handleExportExcel = () => {
        const wb = XLSX.utils.book_new();

        // 1. MARKS SHEET
        const mainHeaders = ['First Name', 'Last Name'];
        sections.forEach(s => {
            mainHeaders.push(`${s.section_name} (${s.max_mark})`);
            mainHeaders.push(`${s.section_name} Lvl`);
        });
        // Added 'Total Marks' to export
        mainHeaders.push('Total Marks', 'Total %', 'Level');

        const mainRows = studentsWithComputed.map(student => {
            const row = [student.first_name, student.last_name];
            sections.forEach(section => {
                const markObj = sectionMarks.find(m => parseInt(m.student_id) === parseInt(student.student_id) && parseInt(m.section_id) === parseInt(section.id));
                const markVal = markObj ? parseFloat(markObj.mark) : null;
                const max = section.max_mark || 100;

                row.push(markVal !== null ? markVal : '');

                let secLevel = '';
                if (markVal !== null && max > 0) {
                    const perc = (markVal / max) * 100;
                    const level = calculateLevel(perc);
                    secLevel = formatGrade(level);
                }
                row.push(secLevel);
            });
            // Add raw totals to export
            row.push(student.rawMaxSum > 0 ? `${student.rawMarkSum}/${student.rawMaxSum}` : '');
            row.push(student.average ? Math.round(student.average) : ''); // Export rounded
            row.push(student.level ? formatGrade(student.level) : '');
            return row;
        });

        const marksSheet = XLSX.utils.aoa_to_sheet([mainHeaders, ...mainRows]);
        XLSX.utils.book_append_sheet(wb, marksSheet, 'Class Marks');

        // 2. SUMMARY SHEET
        const summaryData = [['METRIC', 'VALUE']];
        const failThreshold = (subjectname || '').toLowerCase().includes('home language') ? 50 : 40;
        const passedCount = studentsWithComputed.filter(s => (s.average || 0) >= failThreshold).length;
        const classAvg = studentsWithComputed.reduce((acc, s) => acc + (s.average || 0), 0) / (studentsWithComputed.length || 1);

        summaryData.push(['Class Average', classAvg.toFixed(2) + '%']);
        summaryData.push(['Pass Rate', ((passedCount / (students.length || 1)) * 100).toFixed(1) + '%']);
        summaryData.push(['Total Students', students.length]);

        // Per Section Stats
        summaryData.push([], ['SECTION PERFORMANCE'], ['Section', 'Avg Mark', 'Avg %', 'Highest', 'Lowest']);
        sections.forEach(sec => {
            const stats = getSectionStats(sec);
            if (stats) {
                summaryData.push([
                    sec.section_name,
                    stats.avg.toFixed(2),
                    stats.avgPerc.toFixed(1) + '%',
                    stats.max,
                    stats.min
                ]);
            } else {
                summaryData.push([sec.section_name, '-', '-', '-', '-']);
            }
        });

        // 3. LEVEL DISTRIBUTION LOGIC
        const levelCounts = {
            Overall: { 7: 0, 6: 0, 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
        };
        sections.forEach(s => {
            levelCounts[s.id] = { 7: 0, 6: 0, 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        });

        studentsWithComputed.forEach(student => {
            if (student.level) levelCounts.Overall[student.level]++;
            sections.forEach(section => {
                const markObj = sectionMarks.find(m => parseInt(m.student_id) === parseInt(student.student_id) && parseInt(m.section_id) === parseInt(section.id));
                const markVal = markObj ? parseFloat(markObj.mark) : null;
                const max = section.max_mark || 100;

                if (markVal !== null && max > 0) {
                    const perc = (markVal / max) * 100;
                    const lvl = calculateLevel(perc);
                    if (levelCounts[section.id]) levelCounts[section.id][lvl]++;
                }
            });
        });

        summaryData.push([], [], ['LEVEL DISTRIBUTIONS']);
        const distHeaders = ['Level', 'Range', 'OVERALL TOTAL'];
        sections.forEach(s => distHeaders.push(s.section_name));
        summaryData.push(distHeaders);

        const levelsDesc = [
            { l: 7, r: '80-100%' },
            { l: 6, r: '70-79%' },
            { l: 5, r: '60-69%' },
            { l: 4, r: '50-59%' },
            { l: 3, r: '40-49%' },
            { l: 2, r: '30-39%' },
            { l: 1, r: '0-29%' }
        ];

        levelsDesc.forEach(item => {
            const row = [item.l, item.r];
            row.push(levelCounts.Overall[item.l]);
            sections.forEach(s => {
                row.push(levelCounts[s.id][item.l]);
            });
            summaryData.push(row);
        });

        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary Stats');

        XLSX.writeFile(wb, `${(subjectname || 'Subject').replace(/\s+/g, '_')}_Master_Report.xlsx`);
    };

    const handleDownloadTemplate = () => {
        if (!sections.length && !students.length) { alert('Add sections or students first.'); return; }
        const headers = ['First Name', 'Last Name', ...sections.map(s => s.section_name)];
        const rows = [headers];
        students.forEach(s => {
            const row = [s.first_name, s.last_name];
            sections.forEach(() => row.push(''));
            rows.push(row);
        });
        const csvContent = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Template_${(subjectname || 'Subject').replace(/\s+/g, '_')}.csv`;
        link.click();
    };

    // --- Section Management Handlers ---
    const handleAddSection = async (e) => {
        e.preventDefault();
        if (!newSection.section_name.trim()) return alert('Name required');
        setAddingSection(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                subject_id: id, term: 1, ...newSection
            });
            setNewSection({ section_name: '', weight: 1, max_mark: 100 });
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=1`);
            setSections(res.data.sections || []);
        } catch (e) { alert('Failed'); }
        finally { setAddingSection(false); }
    };

    const handleUpdateSection = (id, field, val) => {
        if (field === 'weight') setSectionWeights(p => ({ ...p, [id]: val }));
        if (field === 'max_mark') setSectionMaxMarks(p => ({ ...p, [id]: val }));

        const timerRef = field === 'weight' ? weightTimersRef : maxMarkTimersRef;
        if (timerRef.current[id]) clearTimeout(timerRef.current[id]);

        timerRef.current[id] = setTimeout(async () => {
            try {
                const section = sections.find(s => s.id === id);
                if (!section) return;

                await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                    id,
                    section_name: section.section_name,
                    weight: field === 'weight' ? val : section.weight,
                    max_mark: field === 'max_mark' ? val : section.max_mark
                });

                setSections(prev => prev.map(s =>
                    s.id === id ? { ...s, [field]: parseFloat(val) } : s
                ));

                if (field === 'weight') {
                    setSectionWeights(prev => { const u = { ...prev }; delete u[id]; return u; });
                }
                if (field === 'max_mark') {
                    setSectionMaxMarks(prev => { const u = { ...prev }; delete u[id]; return u; });
                }

            } catch (e) {
                alert('Update failed. Check your connection.');
            }
        }, 1500);
    };

    const handleDeleteSection = async (sid, name) => {
        if (!window.confirm(`Delete ${name}?`)) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/task_sections.php?id=${sid}`);
            setSections(prev => prev.filter(s => s.id !== sid));
        } catch (e) { alert('Error deleting'); }
    };

    // --- Mark Handlers ---
    const handleSectionMarkChange = (studentId, sectionId, value) => {
        const key = `${studentId}-${sectionId}`;
        let sanitized = value.replace(/[^0-9.]/g, '');
        if (sanitized !== '' && !isNaN(sanitized)) {
            let num = parseFloat(sanitized);
            const section = sections.find(s => s.id === sectionId);
            const max = section?.max_mark || 100;
            if (num > max) num = max;
            sanitized = String(num);
        }

        setMarksBeingEdited(prev => ({ ...prev, [key]: sanitized }));

        setSectionMarks(prev => {
            const idx = prev.findIndex(m => parseInt(m.student_id) === parseInt(studentId) && parseInt(m.section_id) === parseInt(sectionId));
            const newMarks = [...prev];
            if (idx !== -1) newMarks[idx] = { ...newMarks[idx], mark: sanitized };
            else newMarks.push({ student_id: parseInt(studentId), section_id: parseInt(sectionId), mark: sanitized });
            return newMarks;
        });
    };

    const handleMarkBlur = async (studentId, sectionId) => {
        const key = `${studentId}-${sectionId}`;
        const val = marksBeingEdited[key];
        if (val !== undefined && val !== '') {
            try {
                await axios.post(`${process.env.REACT_APP_API_URL}/section_marks.php`, {
                    student_id: studentId, section_id: sectionId, mark: val
                });
                setMarksBeingEdited(prev => { const u = { ...prev }; delete u[key]; return u; });
            } catch (e) { }
        }
    };

    // --- Student Handlers ---
    const handleAddStudent = async () => {
        if (!newStudent.firstName) return alert('Name required');
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/add_student_with_marks.php`, {
                subject_id: id, first_name: newStudent.firstName, last_name: newStudent.lastName
            });
            if (res.data.success) window.location.reload();
        } catch (e) { alert('Error'); }
    };

    const handleDeleteStudent = async (sid, name) => {
        if (!window.confirm(`Remove student ${name || ''}?`)) return;
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/delete_student.php`, { student_id: sid, subject_id: id });
            setStudents(prev => prev.filter(s => s.student_id !== sid));
        } catch (e) { alert('Error'); }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm('Delete ALL DATA?')) return;
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/delete_all_students.php`, { subject_id: id });
            window.location.reload();
        } catch (e) { alert('Error'); }
    };

    // --- Initial Load ---
    useEffect(() => {
        const init = async () => {
            try {
                const [secRes, marksRes, classRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=1`),
                    axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${id}&term=1`),
                    axios.get(`${process.env.REACT_APP_API_URL}/fetch_class_marks.php?subject_id=${id}`)
                ]);

                setSections(secRes.data.sections || []);
                const weights = {}; const maxes = {};
                (secRes.data.sections || []).forEach(s => { weights[s.id] = s.weight; maxes[s.id] = s.max_mark; });
                setSectionWeights(weights);
                setSectionMaxMarks(maxes);

                setSectionMarks(marksRes.data.marks || []);

                setStudents((classRes.data.students || []).map(s => ({
                    ...s, level: s.average ? calculateLevel(s.average) : null
                })));
                setSubjectName(classRes.data.subject_name);
                setSubjectGrade(classRes.data.grade);
                setSubjectClass(classRes.data.class_name);

            } catch (e) { }
            setLoading(false);
        };
        init();
    }, [id, calculateLevel]);

    return (
        <div className="subject-detail-wrapper">
            <style>{`
                :root {
                    --primary-green: #4CAF50;
                    --dark-green: #388E3C;
                    --light-green: #E8F5E9;
                    --text-dark: #2c3e50;
                    --text-light: #7f8c8d;
                    --border-color: #ecf0f1;
                    --card-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
                }

                body { background-color: #f8f9fa; font-family: 'Inter', -apple-system, sans-serif; color: var(--text-dark); }
                
                .subject-detail-wrapper {
                    padding: 2rem;
                    margin-left: 220px; 
                    max-width: 1600px;
                }

                .page-header {
                    background: white;
                    padding: 1.5rem 2rem;
                    border-radius: 12px;
                    box-shadow: var(--card-shadow);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                }
                .page-header h1 { margin: 0; font-size: 1.5rem; font-weight: 700; color: var(--text-dark); }
                .badge { background: var(--light-green); color: var(--dark-green); padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.85rem; margin-left: 10px; }

                .management-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 1.5rem;
                    margin-bottom: 2rem;
                }

                .modern-card {
                    background: white;
                    border-radius: 12px;
                    padding: 1.5rem;
                    box-shadow: var(--card-shadow);
                    border: 1px solid var(--border-color);
                    transition: transform 0.2s;
                }
                .card-title { font-size: 1.1rem; font-weight: 600; margin-top: 0; margin-bottom: 1rem; color: var(--text-dark); border-bottom: 2px solid var(--light-green); padding-bottom: 0.5rem; display: inline-block; }

                .modern-input {
                    width: 100%;
                    padding: 0.6rem;
                    border: 1px solid #dfe6e9;
                    border-radius: 6px;
                    font-size: 0.9rem;
                    box-sizing: border-box;
                }
                .modern-input:focus { outline: none; border-color: var(--primary-green); box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1); }
                
                .btn {
                    padding: 0.6rem 1.2rem;
                    border: none;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 0.9rem;
                    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
                }
                .btn-primary { background: var(--primary-green); color: white; width: 100%; }
                .btn-primary:hover:not(:disabled) { background: var(--dark-green); }
                .btn-outline { background: transparent; border: 1px solid var(--primary-green); color: var(--primary-green); width: 100%; }
                .btn-outline:hover:not(:disabled) { background: var(--light-green); }
                .btn-danger { background: #fee2e2; color: #dc2626; width: auto; padding: 0.4rem 0.8rem; font-size: 0.8rem; }
                .btn:disabled { opacity: 0.6; cursor: not-allowed; }

                /* Table */
                .table-container {
                    background: white;
                    border-radius: 12px;
                    box-shadow: var(--card-shadow);
                    overflow-x: auto;
                    border: 1px solid var(--border-color);
                    margin-bottom: 30px;
                }
                .modern-table { width: 100%; border-collapse: collapse; min-width: 800px; }
                .modern-table th {
                    background: #f8f9fa;
                    padding: 1rem;
                    text-align: center;
                    font-weight: 600;
                    color: var(--text-dark);
                    border-bottom: 2px solid var(--border-color);
                    white-space: nowrap;
                    position: sticky; top: 0; z-index: 10;
                }
                .modern-table td {
                    padding: 0.5rem;
                    border-bottom: 1px solid var(--border-color);
                    text-align: center;
                    color: var(--text-dark);
                }
                
                .cell-input {
                    width: 100%;
                    padding: 8px;
                    border: 1px solid transparent; 
                    border-radius: 4px;
                    text-align: center;
                    font-weight: 500;
                    background: transparent;
                }
                .cell-input:focus {
                    background: white;
                    border-color: var(--primary-green);
                    outline: none;
                }
                .cell-input:hover { background: #f1f3f5; }

                /* Stats Grid (Improved Layout) */
                .stats-section-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: var(--text-dark);
                    margin-bottom: 1.5rem;
                    padding-left: 0.75rem;
                    border-left: 4px solid var(--primary-green);
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .analytics-grid {
                    display: grid;
                    /* Increased min-width to prevent squashing */
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 24px;
                }

                .stat-card {
                    background: white;
                    border-radius: 12px;
                    padding: 24px;
                    box-shadow: var(--card-shadow);
                    border: 1px solid var(--border-color);
                    border-top: 4px solid var(--primary-green);
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    min-height: 200px;
                }

                .stat-card h4 {
                    margin: 0 0 20px 0;
                    color: var(--text-dark);
                    font-size: 1.1rem;
                    font-weight: 700;
                    border-bottom: 1px solid #f0f0f0;
                    padding-bottom: 10px;
                }

                .stat-grid-inner {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    column-gap: 20px;
                    row-gap: 16px;
                    margin-bottom: 20px;
                }

                .stat-item {
                    display: flex;
                    flex-direction: column;
                }

                .stat-label {
                    font-size: 0.75rem;
                    color: var(--text-light);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                }

                .stat-value {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: var(--text-dark);
                }

                .stat-value.green { color: var(--primary-green); }
                .stat-value.red { color: #e74c3c; }

                .submission-count {
                    margin-top: auto;
                    padding-top: 15px;
                    border-top: 1px dashed #eee;
                    font-size: 0.85rem;
                    color: var(--text-light);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 500;
                }

                .loading-overlay { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 300px; color: var(--text-light); }
                .spinner { width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid var(--primary-green); border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 1rem; }
                
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                @media (max-width: 900px) {
                    .subject-detail-wrapper { margin-left: 0; padding: 1rem; }
                    .page-header { flex-direction: column; gap: 1rem; text-align: center; }
                }
                @media print {
                    .no-print, .subject-detail-wrapper { margin: 0; padding: 0; }
                    .modern-card, .table-container { box-shadow: none; border: 1px solid #ccc; }
                }
            `}</style>

            {/* Header */}
            <div className="page-header">
                <div>
                    <h1>{subjectname} <span className="badge">{subjectGrade} {subjectClass}</span></h1>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn btn-outline no-print" style={{ width: 'auto' }} onClick={() => window.print()}>
                        <Icons.Print /> Print Sheet
                    </button>
                    <button className="btn btn-danger no-print" onClick={handleDeleteAll}>
                        <Icons.Trash /> Reset All
                    </button>
                </div>
            </div>

            <div className="no-print management-grid">

                {/* CARD 1: File Operations */}
                <div className="modern-card">
                    <h3 className="card-title"> Import / Export</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <button className="btn btn-outline" onClick={handleDownloadTemplate}>⬇ CSV Template</button>
                            <button className="btn btn-outline" onClick={handleExportExcel}>⬆ Export Report</button>
                        </div>

                        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px dashed #ced4da', textAlign: 'center' }}>
                            {csvUploading ? (
                                <div style={{ color: 'var(--primary-green)', fontWeight: '600' }}>
                                    <div className="spinner" style={{ width: '20px', height: '20px', display: 'inline-block', verticalAlign: 'middle', marginRight: '8px', marginBottom: 0 }}></div>
                                    {csvProgress}
                                </div>
                            ) : (
                                <>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: '#666' }}>Upload Filled Data (Excel/CSV)</label>
                                    <input type="file" accept=".xlsx, .xls, .csv" onChange={handleExcelUpload} className="modern-input" />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* CARD 2: Add Student */}
                <div className="modern-card">
                    <h3 className="card-title"> Add Student</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input className="modern-input" placeholder="First Name" value={newStudent.firstName} onChange={e => setNewStudent({ ...newStudent, firstName: e.target.value })} />
                        <input className="modern-input" placeholder="Last Name" value={newStudent.lastName} onChange={e => setNewStudent({ ...newStudent, lastName: e.target.value })} />
                        <button className="btn btn-primary" onClick={handleAddStudent}>+ Add Student</button>
                    </div>
                </div>

                {/* CARD 3: Manage Sections */}
                <div className="modern-card" style={{ gridColumn: '1 / -1' }}>
                    <h3 className="card-title"> Assessment Sections</h3>

                    {/* Add Section Bar */}
                    <form onSubmit={handleAddSection} style={{ display: 'flex', gap: '10px', alignItems: 'center', background: '#f8f9fa', padding: '10px', borderRadius: '8px', marginBottom: '15px' }}>
                        <input className="modern-input" placeholder="New Section Name (e.g. Test 1)" style={{ flex: 2 }} value={newSection.section_name} onChange={e => setNewSection({ ...newSection, section_name: e.target.value })} />
                        <input type="number" className="modern-input" placeholder="Curriculum Agnostic Weighting" style={{ flex: 1 }} value={newSection.weight} onChange={e => setNewSection({ ...newSection, weight: e.target.value })} />
                        <input type="number" className="modern-input" placeholder="Max" style={{ flex: 1 }} value={newSection.max_mark} onChange={e => setNewSection({ ...newSection, max_mark: e.target.value })} />
                        <button className="btn btn-primary" style={{ width: 'auto', padding: '0.6rem 1.5rem' }} disabled={addingSection}>Add</button>
                    </form>

                    {/* Section List */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                        {sections.map(s => (
                            <div key={s.id} style={{ background: 'white', border: '1px solid #eee', borderRadius: '8px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <div style={{ fontWeight: '600', color: 'var(--text-dark)' }}>{s.section_name}</div>
                                <div style={{ display: 'flex', gap: '5px', fontSize: '0.8rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', color: '#999' }}>Curriculum Agnostic Weighting</label>
                                        <input className="modern-input" type="number" step="0.1" value={sectionWeights[s.id] ?? s.weight} onChange={e => handleUpdateSection(s.id, 'weight', e.target.value)} style={{ padding: '4px' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', color: '#999' }}>Max</label>
                                        <input className="modern-input" type="number" value={sectionMaxMarks[s.id] ?? s.max_mark} onChange={e => handleUpdateSection(s.id, 'max_mark', e.target.value)} style={{ padding: '4px' }} />
                                    </div>
                                </div>
                                <button onClick={() => handleDeleteSection(s.id, s.section_name)} style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: '0.8rem', cursor: 'pointer', textAlign: 'right', marginTop: '5px' }}>Delete Section</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Data Table */}
            {loading ? (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Loading marks data...</p>
                </div>
            ) : (
                <>
                    <div className="table-container">
                        <div style={{ padding: '15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: '600', color: '#666' }}>{students.length} Students Enrolled</span>
                            <button className="btn btn-primary no-print" style={{ width: 'auto' }} onClick={handleBulkSave} disabled={saveAllDisabled}>
                                <Icons.Save /> {saveAllDisabled ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>

                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', paddingLeft: '1.5rem', width: '200px' }}>Student Name</th>
                                    {sections.map(s => (
                                        <th key={s.id}>
                                            <div style={{ fontSize: '0.95rem' }}>{s.section_name}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#999', fontWeight: '400' }}>Max: {s.max_mark}</div>
                                        </th>
                                    ))}
                                    {/* --- MODIFIED: Added Total Header --- */}
                                    <th style={{ width: '100px', background: '#e8f5e9', borderLeft: '2px solid #dfe6e9' }}>Total</th>
                                    <th style={{ width: '80px', background: '#e8f5e9' }}>Total %</th>
                                    <th style={{ width: '60px', background: '#e8f5e9' }}>Level</th>
                                    <th className="no-print" style={{ width: '60px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {studentsWithComputed.map(student => (
                                    <tr key={student.student_id}>
                                        <td style={{ textAlign: 'left', paddingLeft: '1.5rem', fontWeight: '500' }}>
                                            {student.first_name} {student.last_name}
                                        </td>
                                        {sections.map(s => {
                                            const key = `${student.student_id}-${s.id}`;
                                            const markObj = sectionMarks.find(m => parseInt(m.student_id) === parseInt(student.student_id) && parseInt(m.section_id) === parseInt(s.id));
                                            const val = marksBeingEdited[key] !== undefined ? marksBeingEdited[key] : (markObj?.mark ?? '');

                                            // Ref setup
                                            if (!markInputRefs.current[s.id]) markInputRefs.current[s.id] = {};
                                            if (!markInputRefs.current[s.id][student.student_id]) markInputRefs.current[s.id][student.student_id] = React.createRef();

                                            return (
                                                <td key={s.id} style={{ padding: 0 }}>
                                                    <input
                                                        ref={markInputRefs.current[s.id][student.student_id]}
                                                        className="cell-input"
                                                        value={val}
                                                        placeholder="-"
                                                        onChange={e => handleSectionMarkChange(student.student_id, s.id, e.target.value)}
                                                        onBlur={() => handleMarkBlur(student.student_id, s.id)}
                                                        onKeyDown={e => handleGridNavigation(e, student.student_id, s.id)}
                                                    />
                                                </td>
                                            );
                                        })}

                                        {/* --- MODIFIED: Added Raw Total Column --- */}
                                        <td style={{ fontWeight: 'bold', color: '#555', background: '#f8f9fa', borderLeft: '2px solid #dfe6e9' }}>
                                            {student.rawMaxSum > 0 ? (
                                                <span>{Number(student.rawMarkSum).toFixed(1)} <span style={{ fontSize: '0.8em', color: '#999' }}>/ {student.rawMaxSum}</span></span>
                                            ) : '-'}
                                        </td>

                                        {/* --- MODIFIED: Changed .toFixed to Math.round for display to fix 75% vs 76% issue --- */}
                                        <td style={{ background: '#fcfcfc', fontWeight: 'bold', color: student.average >= 50 ? 'var(--primary-green)' : '#e74c3c' }}>
                                            {student.average !== null ? Math.round(student.average) + '%' : '-'}
                                        </td>

                                        <td style={{ background: '#fcfcfc', fontWeight: 'bold' }}>
                                            <span style={{
                                                display: 'inline-block', width: '24px', height: '24px', lineHeight: '24px', borderRadius: '50%',
                                                background: student.level >= 4 ? 'var(--light-green)' : '#fee2e2',
                                                color: student.level >= 4 ? 'var(--dark-green)' : '#dc2626',
                                                fontSize: '0.85rem'
                                            }}>
                                                {formatGrade(student.level)}
                                            </span>
                                        </td>
                                        <td className="no-print">
                                            <button className="btn-danger" style={{ borderRadius: '4px', padding: '4px 8px' }} onClick={() => handleDeleteStudent(student.student_id, student.first_name)}>×</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Performance Analytics Section */}
                    {sections.length > 0 && (
                        <div className="no-print" style={{ marginTop: '40px' }}>
                            <h3 className="stats-section-title"><Icons.Chart /> Performance Analytics</h3>
                            <div className="analytics-grid">
                                {sections.map(section => {
                                    const stats = getSectionStats(section);
                                    if (!stats) return null;

                                    return (
                                        <div key={section.id} className="stat-card">
                                            <h4>{section.section_name}</h4>
                                            <div className="stat-grid-inner">
                                                <div className="stat-item">
                                                    <span className="stat-label">Average Mark</span>
                                                    <span className="stat-value">{stats.avg.toFixed(1)}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Avg %</span>
                                                    <span className="stat-value green">{stats.avgPerc.toFixed(1)}%</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Highest</span>
                                                    <span className="stat-value">{stats.max}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Lowest</span>
                                                    <span className="stat-value red">{stats.min}</span>
                                                </div>
                                            </div>
                                            <div className="submission-count">
                                                <Icons.Check /> Submitted: <strong>{stats.count}</strong> / {students.length} students
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}